<template>
  <div class="header-container">
    <div class="header-container-logo">
      <img :src="logoSrc" alt="Logo"/>
    </div>

    <div class="header-container-navigation-item">
      <nav class="navigation">
        <template v-for="link in primaryLinks" :key="link.path">
          <a
              v-if="link.external || link.disabled"
              :href="link.external ? link.path : undefined"
              :class="{
                'active-link': isLinkActive(link),
                'disabled-link': link.disabled
              }"
              :target="link.external ? '_blank' : null"
              :rel="link.external ? 'noopener noreferrer' : null"
              class="navigation-link"
              :tabindex="link.disabled ? -1 : 0"
              @click="onLinkClick($event, link)"
          >
            {{ link.name }}
          </a>
          <router-link
              v-else
              :to="link.path"
              :class="{ 'active-link': isLinkActive(link) }"
              class="navigation-link"
          >
            {{ link.name }}
          </router-link>
        </template>

        <div
            ref="contentMenuRef"
            class="content-menu"
        >
          <button
              class="content-menu-button"
              :class="{
                'content-menu-button--active': isContentRouteActive,
                'content-menu-button--open': isContentMenuOpen
              }"
              type="button"
              @click.stop="toggleContentMenu"
          >
            Контент
            <span class="content-menu-caret" />
          </button>

          <div v-if="isContentMenuOpen" class="content-menu-dropdown">
            <div v-if="isLoadingContentPages" class="content-menu-state">
              Загрузка...
            </div>
            <div v-else-if="contentPagesError" class="content-menu-state content-menu-state--error">
              {{ contentPagesError }}
            </div>
            <div v-else-if="!contentPages.length" class="content-menu-state">
              Нет страниц
            </div>
            <template v-else>
              <router-link
                  v-for="page in contentPages"
                  :key="page.id"
                  :to="{ name: 'Content', params: { slug: page.slug } }"
                  class="content-menu-link"
                  :class="{ 'content-menu-link--active': route.path === `/content/${page.slug}` }"
                  @click="closeContentMenu"
              >
                {{ page.title }}
              </router-link>
            </template>
          </div>
        </div>

        <template v-for="link in secondaryLinks" :key="link.path">
          <a
              v-if="link.external || link.disabled"
              :href="link.external ? link.path : undefined"
              :class="{
                'active-link': isLinkActive(link),
                'disabled-link': link.disabled
              }"
              :target="link.external ? '_blank' : null"
              :rel="link.external ? 'noopener noreferrer' : null"
              class="navigation-link"
              :tabindex="link.disabled ? -1 : 0"
              @click="onLinkClick($event, link)"
          >
            {{ link.name }}
          </a>
          <router-link
              v-else
              :to="link.path"
              :class="{ 'active-link': isLinkActive(link) }"
              class="navigation-link"
          >
            {{ link.name }}
          </router-link>
        </template>
      </nav>
    </div>

    <div class="header-container-login-item" v-if="!isAuthenticated">
      <router-link to="/login" class="header-container-item-login-link">Войти на сайт</router-link>
    </div>
    <div class="header-container-item-profile" v-else>
      <div class="profile-row">
        <router-link to="/profile">
          <img :src="profilePictureUrl" alt="Profile Picture" class="profile-picture"/>
        </router-link>
        <span>{{ user.name }}</span>
        <button @click="logoutAction" class="logout-button">Выйти</button>
      </div>
      <p class="last-update-text">{{ lastUpdateDate }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LogoImage from '@/assets/images/am_logo 1.svg'
import ProfilePicture from '@/assets/images/profile-picture-example.svg'
import { fetchWordPressPages, generateDatalensToken, getLastDatasetUpdateDate, logout } from '@/utils/requests'
import { useStore } from 'vuex'

const route = useRoute()
const store = useStore()
const router = useRouter()

const logoSrc = ref(LogoImage)
const primaryLinks = ref([
  { name: 'СМИ', path: 'https://automarketolog.ru/', external: true },
  { name: 'Чат', path: '/chat' },
  { name: 'Аналитика', path: '/analytics' },
])
const secondaryLinks = ref([
  { name: 'Поиск по VIN', path: '/analytics1', disabled: true },
  { name: 'ИНН поиск', path: '/analytics2', disabled: true },
])
const contentPages = ref([])
const isContentMenuOpen = ref(false)
const isLoadingContentPages = ref(false)
const contentPagesError = ref('')
const contentMenuRef = ref(null)

const user = computed(() => store.getters['main/userInfo'] || {})
const isContentRouteActive = computed(() => route.path.startsWith('/content'))

const isAuthenticated = computed(() => !!localStorage.getItem('token'))

const profilePictureUrl = computed(() => {
  return user.value?.profile_picture
      ? `${process.env.VUE_APP_URL_DEV}/storage/${user.value.profile_picture}`
      : ProfilePicture
})

const lastUpdateDateText = 'Дата последнего обновления: '
const lastUpdateDate = ref(`${lastUpdateDateText}--.--.----`)

const isActive = path => route.path.startsWith(path)
const isLinkActive = link => !link.external && isActive(link.path)
const onLinkClick = (event, link) => {
  if (link.disabled) {
    event.preventDefault()
  }
}

const fetchUser = id => store.dispatch('main/fetchUser', id)

const logoutAction = async () => {
  try {
    const response = await logout(localStorage.getItem('token'))
    if (response.ok) {
      localStorage.clear()
      await store.dispatch('main/logout')
      await router.push('/login')
    } else {
      console.error('Ошибка при выходе из системы:', response.statusText)
    }
  } catch (error) {
    console.error('Ошибка при выполнении запроса:', error)
  }
}

const getLastUpdateDate = async () => {
  try {
    const token = localStorage.getItem('token')

    const tokenResponse = await generateDatalensToken(token)
    const tokenJsonData = await tokenResponse.json()
    if (!tokenResponse.ok || !tokenJsonData.data.token) {
      throw new Error('Ошибка при получении токена даталенс:', tokenResponse.status);
    }
    const datalensToken = tokenJsonData.data.token;

    const response = await getLastDatasetUpdateDate(token, datalensToken)
    const responseJsonData = await response.json()
    if (response.ok) {
      console.log(responseJsonData)
      lastUpdateDate.value = lastUpdateDateText + formatDate(new Date(responseJsonData.data.last_update_date))
    } else {
      throw new Error('Ошибка при получении последней даты обновления датасетов: ' + response.status)
    }
  } catch (error) {
    console.error('Ошибка при выполнении запроса:', error)
    lastUpdateDate.value = lastUpdateDateText + formatDate(new Date())
  }
}

const parseResponse = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.data?.message || payload?.message || fallbackMessage)
  }
  return payload?.data || {}
}

const loadContentPages = async () => {
  if (isLoadingContentPages.value) {
    return
  }

  isLoadingContentPages.value = true
  contentPagesError.value = ''

  try {
    const response = await fetchWordPressPages()
    const data = await parseResponse(response, 'Не удалось загрузить страницы.')
    contentPages.value = Array.isArray(data.pages) ? data.pages : []
  } catch (error) {
    contentPagesError.value = error.message || 'Не удалось загрузить страницы.'
    contentPages.value = []
  } finally {
    isLoadingContentPages.value = false
  }
}

const openContentMenu = async () => {
  if (!isAuthenticated.value) {
    return
  }

  isContentMenuOpen.value = true

  if (!contentPages.value.length || contentPagesError.value) {
    await loadContentPages()
  }
}

const closeContentMenu = () => {
  isContentMenuOpen.value = false
}

const toggleContentMenu = async () => {
  if (isContentMenuOpen.value) {
    closeContentMenu()
    return
  }

  await openContentMenu()
}

const handleOutsideClick = (event) => {
  if (!isContentMenuOpen.value || !contentMenuRef.value) {
    return
  }

  if (!contentMenuRef.value.contains(event.target)) {
    closeContentMenu()
  }
}

const formatDate = (date) => {
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  })
}

onMounted(async () => {
  document.addEventListener('click', handleOutsideClick)

  if (!isAuthenticated.value) {
    return
  }

  await fetchUser(user.value?.id)
  await Promise.all([
    getLastUpdateDate(),
    loadContentPages(),
  ])
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
})

watch(
    () => route.fullPath,
    () => {
      closeContentMenu()
    }
)
</script>

<style scoped>
.header-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  padding: 0 20px;
  height: 96px;
  border-bottom: 1px solid rgba(87, 90, 96, 0.2);
  gap: 12px;
}

.header-container-logo {
  justify-self: start;
  min-width: 0;
}

.header-container-logo img {
  display: block;
  max-width: 100%;
  height: auto;
}

@media (max-width: 1280px) {
  .header-container {
    padding: 0 14px;
  }

  .header-container-logo img {
    max-width: 200px;
  }
}

@media (max-width: 960px) {
  .header-container-logo img {
    max-width: 150px;
  }
}

.header-container-navigation-item {
  justify-self: center;
}

.navigation {
  display: flex;
  align-items: center;
}

.navigation-link {
  margin: 0 12px;
  display: inline-flex;
  align-items: center;
  height: 96px;
  box-sizing: border-box;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  color: #23282C;
  line-height: 16px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  font-family: inherit;
  padding: 0;
  white-space: nowrap;
}

@media (max-width: 1280px) {
  .navigation-link {
    margin: 0 8px;
    font-size: 14px;
  }
}

@media (max-width: 1100px) {
  .navigation-link.disabled-link {
    display: none;
  }
}

@media (max-width: 960px) {
  .navigation-link {
    margin: 0 6px;
    font-size: 13px;
  }
}

.navigation-link:hover {
  cursor: pointer;
  color: red;
}

.navigation-link.active-link {
  color: #198FA0;
  border-bottom-color: #198FA0;
}

.navigation-link.disabled-link {
  color: gray;
  cursor: not-allowed;
}

.content-menu {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 12px;
}

@media (max-width: 1280px) {
  .content-menu {
    margin: 0 8px;
  }
}

@media (max-width: 960px) {
  .content-menu {
    margin: 0 6px;
  }
}

.content-menu-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  font-family: inherit;
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  color: #23282C;
  cursor: pointer;
  transition: color 0.14s ease, border-color 0.14s ease, background-color 0.14s ease;
  white-space: nowrap;
}

@media (max-width: 1280px) {
  .content-menu-button {
    font-size: 14px;
    padding: 0 10px;
  }
}

@media (max-width: 960px) {
  .content-menu-button {
    font-size: 13px;
    padding: 0 8px;
  }
}

.content-menu-button:hover {
  color: #198FA0;
}

.content-menu-button--active {
  color: #198FA0;
}

.content-menu-button--open {
  color: #198FA0;
  background: #fff;
  border-color: #d9dce1;
  border-bottom-color: #fff;
  border-radius: 12px 12px 0 0;
  position: relative;
  z-index: 6;
}

.content-menu-caret {
  width: 8px;
  height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg) translateY(-1px);
  transition: transform 0.16s ease;
}

.content-menu-button--open .content-menu-caret {
  transform: rotate(225deg) translateY(-1px);
}

.content-menu-dropdown {
  position: absolute;
  top: calc(100% - 1px);
  left: 0;
  min-width: 240px;
  max-width: 360px;
  width: max-content;
  margin-top: 0;
  background: #fff;
  border: 1px solid #d9dce1;
  box-shadow: 0 12px 32px rgba(21, 26, 34, 0.14);
  border-top: none;
  border-radius: 0 12px 12px 12px;
  padding: 6px 0;
  overflow: hidden;
  z-index: 5;
  animation: content-menu-enter 0.16s ease-out;
  transform-origin: top left;
}

.content-menu-link {
  display: block;
  color: #23282c;
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  padding: 9px 14px;
  transition: background-color 0.14s ease, color 0.14s ease;
  white-space: normal;
  word-break: break-word;
}

.content-menu-link:hover {
  background-color: #f1f6f7;
}

.content-menu-link--active {
  color: #198FA0;
  font-weight: 600;
}

.content-menu-state {
  padding: 8px 14px;
  font-size: 14px;
  line-height: 18px;
  color: #23282c;
}

.content-menu-state--error {
  color: #ba2a2a;
}

@keyframes content-menu-enter {
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.99);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.header-container-login-item {
  height: 56px;
  border-radius: 4px;
  padding: 12px 20px;
  border: 1px solid #23282C;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: end;
}

.header-container-item-login-link {
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  color: #23282C;
  text-decoration: none;
}

.header-container-item-profile {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-self: end;
  min-width: 320px;
}

@media (max-width: 1280px) {
  .header-container-item-profile {
    min-width: 0;
  }

  .header-container-item-profile span {
    display: none;
  }
}

@media (max-width: 1100px) {
  .last-update-text {
    display: none;
  }
}

.profile-row {
  display: flex;
  align-items: center;
}

.header-container-item-profile span {
  margin-left: 12px;
  font-weight: 400;
  font-size: 16px;
  color: #23282C;
}

.profile-picture {
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

.logout-button {
  margin-left: 12px;
  background-color: #198FA0;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
}

.logout-button:hover {
  background-color: #136f80;
}

.last-update-text {
  margin-top: 4px;
  font-size: 12px;
  color: gray;
  min-height: 14px;
}
</style>
